// Adapters from platform-specific season payloads → the normalized matchup
// schema (RECAP_FEATURE_BRIEF.md §5) consumed by funStats / seasonStats.
//
// Each platform has a sleeper-prefixed and yahoo-prefixed pair: ToMatchups
// emits the per-team row array, ToChampionships emits the per-year
// { champion, runnerUp, third } object for funStats.options.championshipsByYear.

import { historicalTeam } from "./historicalTeams.js";
import { yahooOwnerIdentity } from "./aliasHeuristics.js";

const DEFAULT_PLAYOFF_WEEK_START = 15;

/**
 * Convert fetchSleeperLeagueHistory() output into a flat normalized matchup array.
 * Two rows per matchup pair (one per team), schema:
 *   { year, week, owner, pts, opp, opp_pts, won, playoff, consolation }
 */
export function sleeperToMatchups(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return [];

  const canonicalName = buildCanonicalNameMap(seasons, options.aliasMap);
  const out = [];

  for (const season of seasons) {
    const year = String(season.season);
    const playoffWeekStart = season.settings?.playoff_week_start ?? DEFAULT_PLAYOFF_WEEK_START;
    // Median/average-match detection — Sleeper exposes binary flag in settings.
    // Same flag covers both median and average variants; default semantics:
    // median (more common per RECAP spec).
    const medianFormat = Number(season.settings?.league_average_match) === 1;
    const rosterIdToOwner = buildRosterMap(season, canonicalName);
    const consolationGames = bracketGameKeys(season.losersBracket, playoffWeekStart);
    // A1.6 R2 #13 — per-bracket-game label map. Keys mirror `gameKey`
    // (week + sorted roster pair). Values carry the placement (p) and
    // round (r) fields straight from Sleeper's winners bracket so the
    // renderer can show "Championship", "Semifinal", "3rd Place", etc.
    const playoffMeta = bracketGameMeta(season.winnersBracket, playoffWeekStart);
    const maxRound = playoffMeta.maxRound;

    for (const [weekStr, weekRows] of Object.entries(season.matchupsByWeek || {})) {
      const week = Number(weekStr);
      const isPlayoff = week >= playoffWeekStart;
      const rows = adaptWeek(weekRows, year, week, rosterIdToOwner, isPlayoff, consolationGames, medianFormat, playoffMeta.byKey, maxRound);
      for (const r of rows) out.push(r);
    }
  }

  return out;
}

// Latest known display_name per user_id. seasons are newest-first, so first
// occurrence wins.
function buildCanonicalNameMap(seasons, aliasMap = {}) {
  const map = {};
  for (const season of seasons) {
    for (const u of season.users || []) {
      if (u.user_id && !map[u.user_id]) {
        const raw = u.display_name || `User ${u.user_id}`;
        // Alias mapping is { raw → canonical } from the alias UI. Falls
        // through to raw when no alias was set.
        map[u.user_id] = aliasMap[raw] || raw;
      }
    }
  }
  return map;
}

function buildRosterMap(season, canonicalName) {
  const m = {};
  for (const r of season.rosters || []) {
    if (r.owner_id && canonicalName[r.owner_id]) {
      m[r.roster_id] = canonicalName[r.owner_id];
    } else {
      // Orphaned roster (abandoned mid-season, co-owned, etc.) — keep it
      // identifiable so stats still compute rather than dropping the games.
      m[r.roster_id] = `Roster ${r.roster_id}`;
    }
  }
  return m;
}

function adaptWeek(weekRows, year, week, rosterIdToOwner, isPlayoff, consolationGames, medianFormat, playoffMetaByKey, maxRound) {
  if (!Array.isArray(weekRows)) return [];

  const byMatchupId = new Map();
  for (const m of weekRows) {
    if (m.matchup_id == null) continue;
    if (!byMatchupId.has(m.matchup_id)) byMatchupId.set(m.matchup_id, []);
    byMatchupId.get(m.matchup_id).push(m);
  }

  const out = [];
  for (const pair of byMatchupId.values()) {
    if (pair.length !== 2) continue; // bye or malformed
    const [a, b] = pair;
    const ownerA = rosterIdToOwner[a.roster_id];
    const ownerB = rosterIdToOwner[b.roster_id];
    if (!ownerA || !ownerB) continue;

    const key = gameKey(week, a.roster_id, b.roster_id);
    const consolation = isPlayoff && consolationGames.has(key);
    const playoffMeta = isPlayoff && !consolation && playoffMetaByKey ? playoffMetaByKey.get(key) : null;
    const playoffLabel = playoffMeta ? sleeperBracketLabel(playoffMeta, maxRound) : null;
    const pa = Number(a.points) || 0;
    const pb = Number(b.points) || 0;

    out.push({
      year, week,
      owner: ownerA, pts: pa,
      opp: ownerB, opp_pts: pb,
      won: pa > pb,
      playoff: isPlayoff,
      consolation,
      medianFormat: !!medianFormat,
      playoffLabel,
    });
    out.push({
      year, week,
      owner: ownerB, pts: pb,
      opp: ownerA, opp_pts: pa,
      won: pb > pa,
      playoff: isPlayoff,
      consolation,
      medianFormat: !!medianFormat,
      playoffLabel,
    });
  }
  return out;
}

function gameKey(week, ra, rb) {
  return ra < rb ? `${week}|${ra}-${rb}` : `${week}|${rb}-${ra}`;
}

// Resolves every bracket entry into a (week, roster-pair) key. Handles
// t1_from / t2_from references to earlier rounds by iterating until no new
// entries resolve. Returns a Set of game keys.
function bracketGameKeys(bracket, playoffWeekStart) {
  const result = new Set();
  if (!Array.isArray(bracket) || !bracket.length) return result;

  const resolved = new Map(); // bracket m -> { w, l, t1, t2 }
  let changed = true;
  let safety = 0;

  while (changed && safety++ < 10) {
    changed = false;
    for (const e of bracket) {
      if (resolved.has(e.m)) continue;
      const t1 = resolveTeam(e.t1, e.t1_from, resolved);
      const t2 = resolveTeam(e.t2, e.t2_from, resolved);
      if (t1 != null && t2 != null) {
        resolved.set(e.m, { w: e.w, l: e.l, t1, t2 });
        const week = playoffWeekStart + (e.r ?? 1) - 1;
        result.add(gameKey(week, t1, t2));
        changed = true;
      }
    }
  }
  return result;
}

// A1.6 R2 #13 — like bracketGameKeys, but also captures each bracket
// entry's p (placement: 1=championship, 3=3rd, 5=5th, ...) and r (round).
// Returns { byKey: Map<gameKey, {p, r}>, maxRound: number }. The renderer
// combines these with maxRound to label rows as "Championship",
// "Semifinal", "Quarterfinal", "3rd Place", etc.
function bracketGameMeta(bracket, playoffWeekStart) {
  const byKey = new Map();
  let maxRound = 0;
  if (!Array.isArray(bracket) || !bracket.length) return { byKey, maxRound };

  const resolved = new Map();
  let changed = true;
  let safety = 0;
  while (changed && safety++ < 10) {
    changed = false;
    for (const e of bracket) {
      if (resolved.has(e.m)) continue;
      const t1 = resolveTeam(e.t1, e.t1_from, resolved);
      const t2 = resolveTeam(e.t2, e.t2_from, resolved);
      if (t1 != null && t2 != null) {
        resolved.set(e.m, { w: e.w, l: e.l, t1, t2 });
        const round = e.r ?? 1;
        if (round > maxRound) maxRound = round;
        const week = playoffWeekStart + round - 1;
        byKey.set(gameKey(week, t1, t2), { p: e.p ?? null, r: round });
        changed = true;
      }
    }
  }
  return { byKey, maxRound };
}

// Translates Sleeper's {p, r} bracket fields into a human label. Placement
// games (p set) take priority over round names — "3rd Place" is more
// informative than "Final" when both apply.
function sleeperBracketLabel(meta, maxRound) {
  if (!meta) return null;
  const { p, r } = meta;
  if (p === 1) return "Championship";
  if (p === 3) return "3rd Place";
  if (p === 5) return "5th Place";
  if (p === 7) return "7th Place";
  if (typeof p === "number" && p > 0) return `${p}th Place`;
  if (maxRound > 0 && r === maxRound) return "Final";
  if (maxRound > 1 && r === maxRound - 1) return "Semifinal";
  if (maxRound > 2 && r === maxRound - 2) return "Quarterfinal";
  if (r != null) return `Round ${r}`;
  return null;
}

function resolveTeam(direct, from, resolved) {
  if (direct != null) return direct;
  if (!from) return null;
  if (from.w != null) {
    const src = resolved.get(from.w);
    return src ? src.w : null;
  }
  if (from.l != null) {
    const src = resolved.get(from.l);
    return src ? src.l : null;
  }
  return null;
}

/**
 * A2 - Normalize one Sleeper matchup entry into the lineup schema documented
 * in LINEUP_DETAIL_PHASE_1.md. Reads the per-player fields that Sleeper's
 * /league/{id}/matchups/{week} endpoint already returns (no extra fetch).
 *
 * matchupEntry: a single roster's entry from season.matchupsByWeek[week].
 * playersMap: dictionary keyed by player_id with { full_name, first_name,
 *   last_name, position, team }. Sourced from getSleeperPlayersMap().
 *
 * Returns { totalPoints, starters: [...], bench: [...] } where each player
 * row is { playerId, name, position, nflTeam, points }. The starters array
 * preserves Sleeper's starter slot order (QB, RB, RB, WR, ..., DEF), which
 * is also the league's roster_positions order.
 */
export function sleeperToLineup(matchupEntry, playersMap, season) {
  if (!matchupEntry) return null;
  const starters = Array.isArray(matchupEntry.starters) ? matchupEntry.starters : [];
  const startersPoints = Array.isArray(matchupEntry.starters_points) ? matchupEntry.starters_points : [];
  const playersPoints = matchupEntry.players_points || {};
  const allPlayers = Array.isArray(matchupEntry.players) ? matchupEntry.players : [];

  const startersOut = starters.map((pid, idx) => {
    const meta = playersMap?.[pid] || {};
    const name = meta.full_name || `${meta.first_name || ''} ${meta.last_name || ''}`.trim() || `Player ${pid}`;
    return {
      playerId: String(pid),
      name,
      position: meta.position || null,
      // #22 — season-accurate team; falls back to current-state on miss.
      nflTeam: historicalTeam(name, meta.position, season) ?? (meta.team || null),
      points: Number(startersPoints[idx] ?? playersPoints[pid] ?? 0),
    };
  });

  const starterSet = new Set(starters.map(String));
  const bench = allPlayers
    .map(String)
    .filter(pid => !starterSet.has(pid))
    .map(pid => {
      const meta = playersMap?.[pid] || {};
      const name = meta.full_name || `${meta.first_name || ''} ${meta.last_name || ''}`.trim() || `Player ${pid}`;
      return {
        playerId: pid,
        name,
        position: meta.position || null,
        nflTeam: historicalTeam(name, meta.position, season) ?? (meta.team || null),
        points: Number(playersPoints[pid] ?? 0),
      };
    });

  return {
    totalPoints: Number(matchupEntry.points) || 0,
    starters: startersOut,
    bench,
  };
}

/**
 * A2 - Normalize one Yahoo /api/yahoo/lineup proxy response into the same
 * shape. Yahoo's `selected_position` tells us starter vs bench/IR. Defensive
 * positions arrive as "DEF" or similar - we treat anything not "BN"/"IR" as
 * a starter and preserve Yahoo's order.
 */
export function yahooToLineup(rosterResponse, season) {
  if (!rosterResponse || !Array.isArray(rosterResponse.players)) return null;
  const starters = [];
  const bench = [];
  let totalPoints = 0;
  for (const p of rosterResponse.players) {
    const slot = p.selected_position;
    const isBench = slot === "BN" || slot === "IR";
    const name = p.name || "Unknown";
    const entry = {
      playerId: String(p.player_id || p.player_key || ""),
      name,
      position: slot || p.position || null,
      // #22 — match on the player's real position (p.position = display_position),
      // not the lineup slot. Falls back to current-state team on miss.
      nflTeam: historicalTeam(name, p.position, season) ?? (p.nfl_team || null),
      points: Number(p.points) || 0,
    };
    if (isBench) bench.push({ ...entry, position: slot });
    else {
      starters.push(entry);
      totalPoints += entry.points;
    }
  }
  return { totalPoints, starters, bench };
}

/**
 * Derive { [year]: { champion, runnerUp, third } } from Sleeper winners brackets.
 * Sleeper marks placement games with a `p` field (1 = championship, 3 = third-place);
 * if absent, falls back to the highest-round game. Returns {} for any season whose
 * bracket is empty or unresolvable (e.g., league didn't use Sleeper's bracket feature).
 */
export function sleeperToChampionships(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return {};
  const canonicalName = buildCanonicalNameMap(seasons, options.aliasMap);
  const result = {};

  for (const season of seasons) {
    const year = String(season.season);
    const rosterIdToOwner = buildRosterMap(season, canonicalName);
    const playoffWeekStart = season.settings?.playoff_week_start ?? 15;
    const entry = resolveChampionship(season.winnersBracket, rosterIdToOwner, playoffWeekStart);
    if (entry) result[year] = entry;
  }
  return result;
}

function resolveChampionship(bracket, rosterIdToOwner, playoffWeekStart) {
  if (!Array.isArray(bracket) || !bracket.length) return null;

  const resolved = new Map();
  let changed = true;
  let safety = 0;
  while (changed && safety++ < 10) {
    changed = false;
    for (const e of bracket) {
      if (resolved.has(e.m)) continue;
      const t1 = resolveTeam(e.t1, e.t1_from, resolved);
      const t2 = resolveTeam(e.t2, e.t2_from, resolved);
      if (t1 != null && t2 != null) {
        resolved.set(e.m, { ...e, t1, t2 });
        changed = true;
      }
    }
  }

  let champGame = null;
  for (const g of resolved.values()) {
    if (g.p === 1) { champGame = g; break; }
  }
  if (!champGame) {
    let maxR = 0;
    for (const g of resolved.values()) {
      if ((g.r ?? 0) > maxR) { maxR = g.r; champGame = g; }
    }
  }
  if (!champGame || champGame.w == null) return null;

  let thirdGame = null;
  for (const g of resolved.values()) {
    if (g.p === 3) { thirdGame = g; break; }
  }

  // A2 lineup pre-pull — week of the championship game so the pre-pull
  // step can locate it in season.matchupsByWeek without re-walking the
  // bracket. playoffWeekStart + championship round - 1.
  const champWeek = (playoffWeekStart || 15) + ((champGame.r ?? 1) - 1);

  return {
    champion: rosterIdToOwner[champGame.w] || null,
    runnerUp: rosterIdToOwner[champGame.l] || null,
    third: thirdGame ? (rosterIdToOwner[thirdGame.w] || null) : null,
    week: champWeek,
  };
}

// ============================================================================
// YAHOO ADAPTERS
// ============================================================================

/**
 * Convert Yahoo season payloads → flat normalized matchup array.
 * Each season is expected to carry:
 *   season             — year string ("2024")
 *   teams              — [{ team_key, name, nickname, guid, ... }] (from /api/yahoo/teams)
 *   scoreboardsByWeek  — { 1: [{ team1_key, team1_pts, team2_key, team2_pts,
 *                                is_playoffs, is_consolation }, ...], ... }
 *
 * Seasons should be ordered newest-first so canonical names resolve to the
 * latest known nickname per Yahoo guid.
 */
export function yahooToMatchups(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return [];

  const canonicalName = buildYahooCanonicalNameMap(seasons, options.aliasMap);
  const out = [];

  for (const season of seasons) {
    const year = String(season.season);
    const teamKeyToOwner = buildYahooTeamMap(season, canonicalName);
    // Yahoo returns settings flags as strings ('0'/'1'). Same single-flag
    // semantics as Sleeper — covers both median + average variants.
    const medianFormat = String(season.settings?.uses_median_score ?? '0') === '1';
    // A1.6 R2 #13 — Yahoo doesn't expose a bracket structure. Derive
    // round labels positionally from the unique set of non-consolation
    // playoff weeks: last week = Championship, second-to-last = Semifinal,
    // anything earlier = "Round N". Done per season since playoff week
    // ranges shift year to year (e.g., the 17-game season moved playoffs
    // a week later). Consolation games are excluded from the Closest
    // Playoff Matchups subtable anyway, so they don't need a label here.
    const playoffWeeksSet = new Set();
    for (const matchups of Object.values(season.scoreboardsByWeek || {})) {
      if (!Array.isArray(matchups)) continue;
      for (const m of matchups) {
        if (m.is_playoffs && !m.is_consolation) playoffWeeksSet.add(Number(m.week ?? matchups[0]?.week));
      }
    }
    const playoffWeeks = [...playoffWeeksSet].filter(w => Number.isFinite(w)).sort((a, b) => a - b);
    const yahooLabelForWeek = (week, isPlayoff, isConsolation) => {
      if (!isPlayoff || isConsolation) return null;
      const idx = playoffWeeks.indexOf(week);
      if (idx < 0) return null;
      const fromEnd = playoffWeeks.length - 1 - idx;
      if (fromEnd === 0) return "Championship";
      if (fromEnd === 1) return "Semifinal";
      if (fromEnd === 2) return "Quarterfinal";
      return `Round ${idx + 1}`;
    };

    for (const [weekStr, matchups] of Object.entries(season.scoreboardsByWeek || {})) {
      const week = Number(weekStr);
      if (!Array.isArray(matchups)) continue;
      for (const m of matchups) {
        const ownerA = teamKeyToOwner[m.team1_key];
        const ownerB = teamKeyToOwner[m.team2_key];
        if (!ownerA || !ownerB) continue;
        const pa = Number(m.team1_pts) || 0;
        const pb = Number(m.team2_pts) || 0;
        const playoffLabel = yahooLabelForWeek(week, !!m.is_playoffs, !!m.is_consolation);
        out.push({
          year, week,
          owner: ownerA, pts: pa,
          opp: ownerB, opp_pts: pb,
          won: pa > pb,
          playoff: !!m.is_playoffs,
          consolation: !!m.is_consolation,
          medianFormat,
          playoffLabel,
        });
        out.push({
          year, week,
          owner: ownerB, pts: pb,
          opp: ownerA, opp_pts: pa,
          won: pb > pa,
          playoff: !!m.is_playoffs,
          consolation: !!m.is_consolation,
          medianFormat,
          playoffLabel,
        });
      }
    }
  }

  return out;
}

/**
 * Derive { [year]: { champion, runnerUp, third } } from Yahoo standings.
 * Standings rank reflects final position after playoffs (rank 1 = champion,
 * 2 = runner-up, 3 = third). Simpler than Sleeper's bracket resolution.
 */
export function yahooToChampionships(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return {};
  const canonicalName = buildYahooCanonicalNameMap(seasons, options.aliasMap);
  const result = {};

  for (const season of seasons) {
    const year = String(season.season);
    const teamKeyToOwner = buildYahooTeamMap(season, canonicalName);
    const sorted = [...(season.standings || [])].sort(
      (a, b) => (Number(a.rank) || 999) - (Number(b.rank) || 999)
    );
    if (!sorted.length) continue;
    const champion = (sorted[0] && teamKeyToOwner[sorted[0].team_key]) || null;
    const runnerUp = (sorted[1] && teamKeyToOwner[sorted[1].team_key]) || null;
    const third    = (sorted[2] && teamKeyToOwner[sorted[2].team_key]) || null;
    // A2 lineup pre-pull — championship week is the latest non-consolation
    // playoff week across the season's scoreboards.
    let champWeek = null;
    for (const [wkStr, matchups] of Object.entries(season.scoreboardsByWeek || {})) {
      if (!Array.isArray(matchups)) continue;
      const wk = Number(wkStr);
      const hasFinal = matchups.some(m => m.is_playoffs && !m.is_consolation);
      if (hasFinal && (champWeek == null || wk > champWeek)) champWeek = wk;
    }
    if (champion) result[year] = { champion, runnerUp, third, week: champWeek };
  }

  return result;
}

// Yahoo's stable cross-year owner identifier is the manager `guid` — but Yahoo
// now redacts it to "--hidden--" for every manager, so we key on
// yahooOwnerIdentity (guid when real, else `nickname:<nick>`). nickname is
// freeform and changes — we record the first (latest) nickname per identity as
// the canonical display name; the AliasPanel merges variants.
function buildYahooCanonicalNameMap(seasons, aliasMap = {}) {
  const map = {};
  for (const season of seasons) {
    for (const t of season.teams || []) {
      const id = yahooOwnerIdentity(t);
      if (id && !map[id]) {
        const raw = t.nickname || t.name || `Manager ${String(id).slice(0, 14)}`;
        // Alias mapping is { raw → canonical } from the alias UI. Falls
        // through to raw when no alias was set.
        map[id] = aliasMap[raw] || raw;
      }
    }
  }
  return map;
}

// Maps each team_key to an owner display name for ONE season. Guards the
// same-season collision invariant: nobody owns two teams in a single season, so
// two teams resolving to the same owner name is always an aliasing artifact
// (e.g. raw "PJ" and "Killa Phil" both aliased to canonical "PJ", or two hidden
// nicknames both → "Unknown"). Merging them would double-count W/L/PF/games in
// every downstream aggregation (season standings, career totals, export
// standings — they all group by this owner field). So we keep colliding teams
// DISTINCT: the 2nd+ team falls back to its raw nickname, then a numeric
// suffix. Cross-season linking by identity is unaffected — only this season's
// map de-collides. Optional `collisions` array collects {season, name} notices.
function buildYahooTeamMap(season, canonicalName, collisions = null) {
  const teams = season.teams || [];
  const resolve = (t) => {
    const id = yahooOwnerIdentity(t);
    return (id && canonicalName[id]) || t.nickname || t.name || `Team ${t.team_key}`;
  };
  const rawOf = (t) => t.nickname || t.name || `Team ${t.team_key}`;

  // Group teams by resolved canonical name to find collisions.
  const groups = new Map(); // canonical -> [team, ...] (in season order)
  for (const t of teams) {
    const c = resolve(t);
    if (!groups.has(c)) groups.set(c, []);
    groups.get(c).push(t);
  }

  // Per collision group, pick the KEEPER that holds the canonical label — the
  // team whose raw nickname exactly equals the canonical name (so the "PJ"-raw
  // team keeps "PJ" regardless of processing order). If no raw matches (e.g. a
  // pure alias merge, or hidden nicknames -> "Unknown"), the first team in
  // order keeps it. Everyone else falls back to their raw nickname.
  const keeperKey = new Map(); // canonical -> team_key
  for (const [c, grp] of groups) {
    if (grp.length < 2) continue;
    const keeper = grp.find((t) => rawOf(t) === c) || grp[0];
    keeperKey.set(c, keeper.team_key);
    if (collisions) collisions.push({ season: String(season.season), name: c });
  }

  const m = {};
  const used = new Set();
  // Pass A — assign the canonical label to non-collision teams and each group's
  // keeper. These canonical strings are distinct, so order is irrelevant here.
  for (const t of teams) {
    const c = resolve(t);
    const grp = groups.get(c);
    if (grp.length < 2 || keeperKey.get(c) === t.team_key) {
      m[t.team_key] = c;
      used.add(c);
    }
  }
  // Pass B — non-keeper collision teams fall back to raw nickname, then a
  // numeric suffix if the raw is also taken (identical/hidden raws).
  for (const t of teams) {
    if (m[t.team_key] !== undefined) continue;
    let name = rawOf(t);
    if (used.has(name)) {
      let k = 2;
      while (used.has(`${name} (${k})`)) k++;
      name = `${name} (${k})`;
    }
    used.add(name);
    m[t.team_key] = name;
  }
  return m;
}

// Reports same-season owner collisions so the UI can inform the user that the
// teams were kept separate (informational — separation is always correct, no
// user decision needed). Reuses the exact resolution the stats build uses, so
// the notice matches reality. Returns deduped [{ season, name }], newest first.
export function detectSameSeasonCollisions(seasons, aliasMap = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return [];
  const canonicalName = buildYahooCanonicalNameMap(seasons, aliasMap);
  const collisions = [];
  for (const season of seasons) buildYahooTeamMap(season, canonicalName, collisions);
  const seen = new Set();
  const out = [];
  for (const c of collisions) {
    const key = `${c.season}|${c.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  out.sort((a, b) => String(b.season).localeCompare(String(a.season)));
  return out;
}

// ============================================================================
// DRAFTS (Tier 1 + Tier 2 + UX_REFINEMENTS_v2 §3-4) — Sleeper / Yahoo →
// normalized stats.drafts
// ============================================================================
//
// Shape per draft entry (emitted as ONE entry per Sleeper draft — startup,
// rookie, and annual are all separate entries; the report layer groups them):
//   { draft_id, year, league_id,
//     draftType: 'auction'|'snake'|'linear'|'unknown',
//     draftEventType: 'startup'|'rookie'|'annual',
//     rounds, totalPicks,
//     picks: [{ round, pick, owner, player, position, team, cost,
//              player_id, isKeeper,
//              // UX_REFINEMENTS_v2 §3 trade indicators (snake/linear only):
//              originalOwner, wasTraded,
//              // Tier 2 enrichment (full-coverage years only):
//              seasonStats, expectedPositionRank, valueOverExpected,
//              isBust, isSteal }, ...],
//     statsCoverage: 'full' | 'draft_only' }
//
// Sort: startup first, then by year descending (matches stats.championships.years).

export function sleeperToDrafts(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return [];
  const canonicalName = buildCanonicalNameMap(seasons, options.aliasMap);
  const flat = [];
  for (const season of seasons) {
    const drafts = Array.isArray(season.drafts) ? season.drafts : [];
    if (!drafts.length) continue;
    const rosterToOwner = buildRosterMap(season, canonicalName);
    const seasonStats = season.seasonStats || null;
    // user_id → roster_id for draft_order slot-owner derivation. Sleeper
    // rosters carry owner_id which equals user_id.
    const userIdToRosterId = {};
    for (const r of season.rosters || []) {
      if (r.owner_id) userIdToRosterId[r.owner_id] = r.roster_id;
    }
    for (const d of drafts) {
      const picks = [];
      for (const p of d.picks || []) {
        picks.push(buildSleeperPick(p, rosterToOwner, seasonStats, season.season));
      }
      if (!picks.length) continue;
      // Sort chronologically (overallPick) — snake even rounds have slot 10
      // picking BEFORE slot 1, so sorting by `pick` (= slot) would reverse
      // round order in even rounds. overallPick stays linear.
      picks.sort((a, b) => (a.round - b.round) || ((a.overallPick ?? a.pick) - (b.overallPick ?? b.pick)));
      // Sleeper exposes 'snake', 'linear', 'auction'. Both snake and linear
      // use the grid view downstream; auction stays as a list. Infer from
      // pick data when type is missing.
      let draftType = d.type || 'unknown';
      if (draftType === 'unknown' && picks.some(p => p.cost != null && p.cost > 0)) {
        draftType = 'auction';
      }
      const rounds = picks.reduce((m, p) => Math.max(m, p.round || 0), 0);
      const hasCoverage = picks.some(p => p.seasonStats);
      const statsCoverage = hasCoverage ? 'full' : 'draft_only';
      if (hasCoverage) annotatePickValues(picks, draftType);

      // Trade detection — derive expected slot owner from draft_order, then
      // flag any pick whose actual owner differs from expected. Catches
      // futures-pick trades and intra-draft trades uniformly without
      // needing the transactions endpoint.
      const slotToOwner = buildSlotToOwnerMap(d, userIdToRosterId, rosterToOwner);
      const teamCount = Object.keys(slotToOwner).length || season.total_rosters || 0;
      annotateTradedPicks(picks, slotToOwner, teamCount, draftType);

      flat.push({
        draft_id: d.draft_id,
        year: String(season.season),
        league_id: season.league_id,
        draftType,
        // draftEventType assigned by classifyDraftEventTypes after all drafts
        // are collected; placeholder until then.
        draftEventType: 'annual',
        rounds,
        totalPicks: picks.length,
        picks,
        statsCoverage,
        platform: 'sleeper',
        // Internal — used by classifyDraftEventTypes then deleted.
        _metadataDraftType: d.metadata?.draft_type || null,
      });
    }
  }
  classifyDraftEventTypes(flat);
  for (const d of flat) delete d._metadataDraftType;
  // Startup first, then by year descending. This puts the founding draft at
  // the top of the section (it's the most consequential event), rookie/
  // annual drafts follow in newest-first chronological order.
  return flat.sort((a, b) => {
    if (a.draftEventType === 'startup' && b.draftEventType !== 'startup') return -1;
    if (b.draftEventType === 'startup' && a.draftEventType !== 'startup') return 1;
    return String(b.year).localeCompare(String(a.year));
  });
}

function buildSleeperPick(p, rosterToOwner, seasonStats, year) {
  // CRITICAL: Sleeper's pick_no is the OVERALL pick number (1..N×rounds).
  // draft_slot is the in-round slot (1..N), constant per owner across all
  // rounds. The grid + trade-detection code derives column from slot, so
  // `pick` MUST be slot. overallPick is preserved separately for any
  // chronology-aware code (sort, in-round-pick label, etc.). Falls back to
  // pick_no when draft_slot is null (e.g., not-yet-drafted auctions).
  const pick = {
    round: p.round,
    pick: p.draft_slot ?? p.pick_no,
    overallPick: p.pick_no,
    owner: rosterToOwner[p.roster_id] || `Roster ${p.roster_id}`,
    player: p.player_name,
    position: p.position,
    // #22 — season-accurate team. Sleeper's p.team is draft-time (usually
    // correct) but falls back to current-state when missing; historical wins
    // and is consistent with the Yahoo path. Falls back to p.team on miss.
    team: historicalTeam(p.player_name, p.position, year) ?? (p.team || null),
    cost: p.amount != null && p.amount > 0 ? p.amount : null,
    player_id: p.player_id,
    isKeeper: !!p.is_keeper,
  };
  if (seasonStats && p.player_id && seasonStats[p.player_id]) {
    const s = seasonStats[p.player_id];
    // pts_half_ppr presence is the real coverage signal — pre-2009
    // entries can have pos_rank populated but zero pts.
    if (s.pts_half_ppr != null) {
      pick.seasonStats = {
        totalPoints: s.pts_half_ppr,
        gamesPlayed: s.gp || 0,
        pointsPerGame: s.gp ? s.pts_half_ppr / s.gp : 0,
        positionRank: s.pos_rank_half_ppr ?? null,
        overallRank: s.rank_half_ppr ?? null,
      };
    }
  }
  return pick;
}

// Sleeper's draft.draft_order is { user_id: slot } (1-indexed slots).
// season.rosters[].owner_id is the same user_id. Composing them gives
// slot → owner-name (alias-canonical via canonicalName).
function buildSlotToOwnerMap(d, userIdToRosterId, rosterToOwner) {
  const out = {};
  const order = d.draft_order;
  if (!order || typeof order !== 'object') return out;
  for (const [userId, slot] of Object.entries(order)) {
    const rosterId = userIdToRosterId[userId];
    const owner = rosterId != null ? rosterToOwner[rosterId] : null;
    if (owner) out[String(slot)] = owner;
  }
  return out;
}

// Compares each pick's actual owner to the slot's expected owner from
// draft_order. When they differ ⇒ wasTraded:true, originalOwner = expected.
// Auctions skip trade detection (no slot concept). `p.pick` is the in-round
// slot (1..N), constant per owner across all rounds in BOTH snake and linear
// drafts — Sleeper's draft_slot field already does the snake math for us, so
// no round-parity conditional is needed here.
function annotateTradedPicks(picks, slotToOwner, teamCount, draftType) {
  if (!teamCount || !Object.keys(slotToOwner).length) return;
  if (draftType === 'auction') return;
  for (const p of picks) {
    const expected = slotToOwner[String(p.pick)];
    if (!expected) continue;
    p.originalOwner = expected;
    p.wasTraded = expected !== p.owner;
  }
}

// UX_REFINEMENTS_v2 §4 — assign 'startup' / 'rookie' / 'annual' across the
// full flat draft list. Trust Sleeper's metadata.draft_type when present;
// otherwise apply the size heuristic (max ≥ 5× median ⇒ startup, rest rookie;
// no outlier ⇒ all annual / redraft).
function classifyDraftEventTypes(flat) {
  if (!flat.length) return;
  const explicit = flat.some(d => d._metadataDraftType === 'startup' || d._metadataDraftType === 'rookie');
  if (explicit) {
    for (const d of flat) {
      if (d._metadataDraftType === 'startup') d.draftEventType = 'startup';
      else if (d._metadataDraftType === 'rookie') d.draftEventType = 'rookie';
      else d.draftEventType = 'annual';
    }
    return;
  }
  if (flat.length < 2) {
    flat[0].draftEventType = 'annual';
    return;
  }
  const counts = flat.map(d => d.totalPicks).slice().sort((a, b) => a - b);
  const median = counts[Math.floor(counts.length / 2)] || 0;
  const max = counts[counts.length - 1] || 0;
  if (median > 0 && max >= median * 5) {
    const startup = flat.reduce((a, b) => b.totalPicks > a.totalPicks ? b : a);
    for (const d of flat) {
      d.draftEventType = d === startup ? 'startup' : 'rookie';
    }
  } else {
    for (const d of flat) d.draftEventType = 'annual';
  }
}

// Compute expectedPositionRank / valueOverExpected / isBust / isSteal for
// every pick that has seasonStats. Picks without seasonStats (rookies who
// didn't play, retired pre-coverage, etc.) are skipped. Snake uses overall
// pick slot; auction uses dollar-rank within the position.
//
// Thresholds: |vae| >= 5 marks bust/steal. These are intentionally loose —
// the trivia gen will pull the TOP examples per draft, so even at 5+ slots
// of value there's plenty of signal.
function annotatePickValues(picks, draftType) {
  if (draftType === 'auction') {
    // Rank by descending cost within each position. Keepers excluded from
    // the ranking pool (they didn't compete for a draft slot) — they get
    // expectedRank from the position-rank they'd be IF priced fresh.
    const byPos = {};
    for (const p of picks) {
      if (p.isKeeper) continue;
      if (!p.position || p.cost == null) continue;
      if (!byPos[p.position]) byPos[p.position] = [];
      byPos[p.position].push(p);
    }
    for (const pos in byPos) {
      byPos[pos].sort((a, b) => (b.cost || 0) - (a.cost || 0));
      byPos[pos].forEach((p, i) => { p.expectedPositionRank = i + 1; });
    }
  } else {
    // Snake — rough position-weighted slot expectation. Pick N overall →
    // expectedRank for RB/WR is ~0.4×N, for QB/TE/K/DEF is ~0.1×N.
    // (Spec table.) Floor of 1 — even pick #1 expects PosRank #1.
    for (const p of picks) {
      if (p.isKeeper) continue;
      if (!p.pick) continue;
      const mult = (p.position === 'RB' || p.position === 'WR') ? 0.4 : 0.1;
      p.expectedPositionRank = Math.max(1, Math.floor(p.pick * mult));
    }
  }
  for (const p of picks) {
    if (p.expectedPositionRank == null || !p.seasonStats || p.seasonStats.positionRank == null) continue;
    // valueOverExpected: positive = outperformed slot, negative = under.
    p.valueOverExpected = p.expectedPositionRank - p.seasonStats.positionRank;
    if (p.valueOverExpected >= 5) p.isSteal = true;
    if (p.valueOverExpected <= -5) p.isBust = true;
  }
}

// ============================================================================
// TRADES (task #38) — Sleeper / Yahoo → normalized stats.trades
// ============================================================================
//
// Per-trade shape (one entry per completed trade, sorted newest-first):
//   {
//     tradeId,
//     season, week,
//     timestamp,          // ms since epoch (null for Yahoo when not provided)
//     platform,           // 'sleeper' | 'yahoo'
//     participants: [
//       {
//         owner,             // canonical alias name
//         assetsReceived: [Asset, ...],
//         assetsSent:     [Asset, ...],
//       },
//       // typically 2 participants — Sleeper supports 3+ for multi-team
//     ],
//   }
//
// Asset shape (discriminated union by `type`):
//   { type: 'player', player_id, playerName, position, team }
//   { type: 'pick',   season, round, originalOwner, description }
//   { type: 'faab',   amount }
//
// Yahoo never has 'pick' or 'faab' assets in standard leagues — only
// 'player'. Sleeper has all three.

export function sleeperToTrades(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return [];
  const canonicalName = buildCanonicalNameMap(seasons, options.aliasMap);
  const out = [];
  for (const season of seasons) {
    const trades = Array.isArray(season.trades) ? season.trades : [];
    if (!trades.length) continue;
    const rosterToOwner = buildRosterMap(season, canonicalName);
    for (const t of trades) {
      const normalized = normalizeSleeperTrade(t, season, rosterToOwner);
      if (normalized) out.push(normalized);
    }
  }
  return out.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
}

function normalizeSleeperTrade(t, season, rosterToOwner) {
  const rosterIds = Array.isArray(t.roster_ids) ? t.roster_ids : [];
  if (!rosterIds.length) return null;

  // Initialize per-participant buckets keyed by roster_id.
  const buckets = {};
  for (const rid of rosterIds) {
    buckets[rid] = {
      owner: rosterToOwner[rid] || `Roster ${rid}`,
      assetsReceived: [],
      assetsSent: [],
    };
  }

  // PLAYERS — adds[player_id] = roster_id_who_got; drops[player_id] =
  // roster_id_who_lost. Sleeper guarantees adds + drops always pair for
  // trades. We iterate adds (receiver-side) and look up the drop counterpart
  // to know who sent.
  const adds = t.adds || {};
  const drops = t.drops || {};
  const playerMeta = t.playerMeta || {};
  for (const pid of Object.keys(adds)) {
    const receiverRid = adds[pid];
    const senderRid = drops[pid];
    const meta = playerMeta[pid] || {};
    const asset = {
      type: 'player',
      player_id: pid,
      playerName: meta.name || pid,
      position: meta.position || null,
      // #22 — season-accurate team; falls back to current-state on miss.
      team: historicalTeam(meta.name, meta.position, season.season) ?? (meta.team || null),
    };
    if (buckets[receiverRid]) buckets[receiverRid].assetsReceived.push(asset);
    if (buckets[senderRid])   buckets[senderRid].assetsSent.push(asset);
  }

  // DRAFT PICKS — each entry has roster_id (ORIGINAL slot-owner, constant
  // across all trades the pick has been through), previous_owner_id (who
  // sent it in THIS trade), owner_id (who received). Keep originalOwner
  // as metadata so the UI can render "2025 R1 (originally Ben's)" context.
  for (const p of t.draft_picks || []) {
    const originalOwner = rosterToOwner[p.roster_id] || `Roster ${p.roster_id}`;
    const senderRid = p.previous_owner_id;
    const receiverRid = p.owner_id;
    const asset = {
      type: 'pick',
      season: String(p.season),
      round: Number(p.round),
      originalOwner,
      description: buildPickDescription(p.season, p.round, originalOwner, rosterToOwner[p.previous_owner_id]),
    };
    if (buckets[receiverRid]) buckets[receiverRid].assetsReceived.push(asset);
    if (buckets[senderRid])   buckets[senderRid].assetsSent.push(asset);
  }

  // FAAB — waiver_budget entries are { sender, receiver, amount }. Sender
  // loses dollars; receiver gains. Multiple entries possible per trade.
  for (const w of t.waiver_budget || []) {
    const asset = { type: 'faab', amount: Number(w.amount) || 0 };
    if (buckets[w.receiver]) buckets[w.receiver].assetsReceived.push(asset);
    if (buckets[w.sender])   buckets[w.sender].assetsSent.push({ ...asset });
  }

  // Drop participants who ended up with nothing on either side (extremely
  // unusual but defensive).
  const participants = Object.values(buckets).filter(
    p => p.assetsReceived.length || p.assetsSent.length
  );
  if (participants.length < 2) return null;

  return {
    tradeId: `sleeper-${t.transaction_id || t.created || Math.random().toString(36).slice(2)}`,
    season: String(season.season),
    week: Number(t.leg) || null,
    timestamp: t.status_updated || t.created || null,
    platform: 'sleeper',
    participants,
  };
}

function buildPickDescription(season, round, originalOwner, previousOwner) {
  // "2025 1st (originally Tim's)" — only mention originalOwner when it
  // differs from the previous owner (who sent it). When original == sender,
  // showing "originally Tim's" is redundant.
  const ordinal = ordinalRound(round);
  if (previousOwner && previousOwner !== originalOwner) {
    return `${season} ${ordinal} (originally ${originalOwner}'s)`;
  }
  return `${season} ${ordinal}`;
}

function ordinalRound(round) {
  const r = Number(round);
  if (!r) return 'pick';
  if (r === 1) return '1st';
  if (r === 2) return '2nd';
  if (r === 3) return '3rd';
  return `${r}th`;
}

// Yahoo trades — no draft picks, no FAAB swaps (Yahoo doesn't support
// trading either). Just player swaps between two teams.
export function yahooToTrades(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return [];
  const canonicalName = buildYahooCanonicalNameMap(seasons, options.aliasMap);
  const out = [];
  for (const season of seasons) {
    const trades = Array.isArray(season.trades) ? season.trades : [];
    if (!trades.length) continue;
    const teamToOwner = buildYahooTeamMap(season, canonicalName);
    for (const t of trades) {
      const normalized = normalizeYahooTrade(t, season, teamToOwner);
      if (normalized) out.push(normalized);
    }
  }
  return out.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
}

function normalizeYahooTrade(t, season, teamToOwner) {
  const players = Array.isArray(t.players) ? t.players : [];
  if (!players.length) return null;

  // Initialize buckets from the source/destination team_keys we see in
  // the players' transaction_data. Yahoo's trader/tradee_team_key fields
  // also identify the two parties but per-player source/destination is
  // the authoritative direction signal.
  const buckets = {};
  const ensure = (teamKey) => {
    if (!teamKey) return null;
    if (!buckets[teamKey]) {
      buckets[teamKey] = {
        owner: teamToOwner[teamKey] || `Team ${teamKey}`,
        assetsReceived: [],
        assetsSent: [],
      };
    }
    return buckets[teamKey];
  };

  for (const p of players) {
    const senderBucket = ensure(p.source_team_key);
    const receiverBucket = ensure(p.destination_team_key);
    const asset = {
      type: 'player',
      player_id: p.player_key,
      playerName: p.name || p.player_key,
      position: p.position || null,
      // #22 — season-accurate team; Yahoo's p.team is current-state only.
      team: historicalTeam(p.name, p.position, season.season) ?? (p.team || null),
    };
    if (receiverBucket) receiverBucket.assetsReceived.push(asset);
    if (senderBucket)   senderBucket.assetsSent.push(asset);
  }

  // Also seed trader/tradee buckets in case a player swap missed them
  // (rare but defensive against transaction_data shape variance).
  ensure(t.trader_team_key);
  ensure(t.tradee_team_key);

  const participants = Object.values(buckets).filter(
    p => p.assetsReceived.length || p.assetsSent.length
  );
  if (participants.length < 2) return null;

  // Yahoo timestamps come back as seconds-since-epoch strings.
  const tsRaw = t.completed_time ? Number(t.completed_time) : null;
  const timestamp = tsRaw ? tsRaw * 1000 : null;

  return {
    tradeId: `yahoo-${t.transaction_key || Math.random().toString(36).slice(2)}`,
    season: String(season.season),
    week: null,  // Yahoo doesn't expose week-of-trade in this endpoint
    timestamp,
    platform: 'yahoo',
    participants,
  };
}

export function yahooToDrafts(seasons, options = {}) {
  if (!Array.isArray(seasons) || !seasons.length) return [];
  const canonicalName = buildYahooCanonicalNameMap(seasons, options.aliasMap);
  const out = [];
  for (const season of seasons) {
    const picks = season.draft?.picks;
    if (!Array.isArray(picks) || !picks.length) continue;
    const teamToOwner = buildYahooTeamMap(season, canonicalName);

    // #22-keeper — per-team kept player_key sets sourced from roster
    // is_keeper.kept (yahooImport). Empty for redraft years. The grid renders
    // pick.isKeeper (DraftBoardGrid.jsx).
    const keptSets = {};
    for (const [tk, keys] of Object.entries(season.keptByTeam || {})) {
      keptSets[tk] = new Set(keys);
    }

    // CRITICAL: Yahoo's `pick` field on draft_result is the OVERALL pick
    // number (1..N×rounds), NOT the in-round slot. Setting `pick` directly
    // from p.pick broke the DraftBoardGrid for round 2+ on Yahoo leagues
    // (the regression DRAFT_GRID_BUGFIX caught for Sleeper but missed for
    // Yahoo). Derive the in-round slot using snake math instead — and keep
    // overallPick available for the grid's display label like the Sleeper
    // path does. teamCount sourced from the highest round-1 pick (most
    // reliable; matches what DraftBoardGrid would infer), falls back to
    // season.num_teams or season.teams.length.
    const hasCostInPicks = picks.some(p => p.cost != null && Number(p.cost) > 0);
    const isAuction = hasCostInPicks;
    let teamCount = 0;
    for (const p of picks) {
      const pn = Number(p.pick) || 0;
      if (Number(p.round) === 1 && pn > teamCount) teamCount = pn;
    }
    if (!teamCount) teamCount = Number(season.num_teams) || (season.teams?.length) || 0;

    const normPicks = picks.map(p => {
      const overallPick = Number(p.pick) || null;
      const round = Number(p.round) || null;
      let slot = overallPick;
      if (!isAuction && overallPick && round && teamCount) {
        // Yahoo redrafts default to snake — even rounds reverse pick order.
        // Slot is constant per owner across rounds (slot 1 = same team in
        // every round); only the round-internal pick order reverses.
        const inRoundIdx = (overallPick - 1) % teamCount;
        slot = round % 2 === 0
          ? teamCount - inRoundIdx       // even (snake): slot N picks first
          : inRoundIdx + 1;              // odd: slot 1 picks first
      }
      return {
        round,
        pick: slot,
        overallPick,
        owner: teamToOwner[p.team_key] || `Team ${p.team_key}`,
        player: p.player_name || p.player_key || 'Unknown',
        position: p.position || null,
        // #22 — season-accurate team; Yahoo's p.team is current-state only.
        team: historicalTeam(p.player_name, p.position, season.season) ?? (p.team || null),
        cost: p.cost != null && Number(p.cost) > 0 ? Number(p.cost) : null,
        player_id: p.player_key,
        // #22-keeper — keeper truth is roster is_keeper.kept (not draftresults).
        // keptSets is per pick's team; empty in redraft years.
        isKeeper: keptSets[p.team_key]?.has(p.player_key) || false,
      };
    }).sort((a, b) => (a.round - b.round) || ((a.overallPick || 0) - (b.overallPick || 0)));

    const rounds = normPicks.reduce((m, p) => Math.max(m, p.round || 0), 0);
    out.push({
      year: String(season.season),
      league_id: season.league_key,
      draftType: isAuction ? 'auction' : 'snake',
      rounds,
      totalPicks: normPicks.length,
      picks: normPicks,
      // Yahoo drafts always render as Tier 1 only — Tier 2 enrichment
      // requires Sleeper player_id ↔ Yahoo player_key mapping, deferred.
      statsCoverage: 'draft_only',
      // Yahoo has no startup/rookie concept (dynasty support there isn't
      // first-class). All Yahoo drafts are annual.
      draftEventType: 'annual',
      // Platform tag — DraftYearCard uses this to render platform-
      // appropriate "draft-only" copy (Yahoo is missing player enrichment
      // because of the Sleeper-id mapping gap, NOT because of the
      // pre-2009 Sleeper stats floor).
      platform: 'yahoo',
    });
  }
  return out.sort((a, b) => String(b.year).localeCompare(String(a.year)));
}
